import { Component, effect, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { TAGPermission } from '../../../models/tagpermission';
import { KeyValuePipe } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { RouterOutlet, RouterLink, RouterLinkActive, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FluidModule } from 'primeng/fluid';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { PasswordModule } from 'primeng/password';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { APIError } from '../../../models/apierror';
import { TAGUser } from '../../../models/taguser';
import { ApiService } from '../../api.service';
import { AuthGuardService } from '../../auth-guard.service';
import { MatIcon } from '../../components/mat-icon/mat-icon';
import { InternalMenuService } from '../../internal-menu-service';

type MenuLink = {
  route: string;
  label: string;
  icon: string;
  permission: string;
  disabled?: boolean;
};

@Component({
  selector: 'app-internal-base-workstation',
  imports: [
    ReactiveFormsModule,
    FluidModule,
    PasswordModule,
    FloatLabelModule,
    InputTextModule,
    MessageModule,
    ButtonModule,
    TranslatePipe,
    ProgressSpinnerModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    KeyValuePipe,
    MatIcon,
  ],
  templateUrl: './internal-base-workstation.html',
  styleUrl: './internal-base-workstation.scss',
})
export class InternalBaseWorkstation {
  @ViewChild('pinInput1') pinInput1: ElementRef;

  public api = inject(ApiService);
  private authGuard = inject(AuthGuardService);
  private menu = inject(InternalMenuService);
  private router = inject(Router);

  apiReady = signal(false);
  loggingIn = signal(false);
  loginError = signal('');

  menuOpen = signal(false);
  shadowVisible = signal(false);

  loginForm = new FormGroup({
    digit1: new FormControl('', [Validators.required]),
    digit2: new FormControl('', [Validators.required]),
    digit3: new FormControl('', [Validators.required]),
    digit4: new FormControl('', [Validators.required]),
  });

  reloadTimer: any = undefined;

  internalMenuEntries: { [key: string]: MenuLink[] } = {
    '': [
      {
        route: '/workstation/dashboard',
        label: 'internal.menu.links.dashboard',
        icon: 'home',
        permission: 'dashboard',
      },
      {
        route: '/workstation/todos',
        label: 'internal.menu.links.todos',
        icon: 'checklist',
        permission: 'todos',
      },
      {
        route: '/workstation/presence',
        label: 'internal.menu.links.presence',
        icon: 'sensor_occupied',
        permission: 'presence',
      },
      {
        route: '/workstation/inventory',
        label: 'internal.menu.links.inventory',
        icon: 'shelves',
        permission: 'inventory',
      },
    ],
  };

  get filteredMenuEntries() {
    let filtered: { [key: string]: MenuLink[] } = {};
    for (let group in this.internalMenuEntries) {
      let filteredItems = this.internalMenuEntries[group].filter((entry) => {
        return this.hasPermission(entry.permission);
      });

      if (filteredItems.length > 0) {
        filtered[group] = filteredItems;
      }
    }
    return filtered;
  }

  isKioskMode() {
    return this.api.user().hasPermission(TAGPermission.kiosk_mode);
  }

  isLoggedIn() {
    return this.api.isLoggedIn();
  }

  hasPermission(group: string): boolean | Promise<boolean> {
    return this.authGuard.canActivateUrl(group);
  }

  constructor() {
    effect(() => {
      if (this.menu.isOpen()) {
        this.shadowVisible.set(true);
        setTimeout(() => {
          this.menuOpen.set(true);
        }, 100);
      } else {
        this.menuOpen.set(false);
        setTimeout(() => {
          this.shadowVisible.set(false);
        }, 300);
      }
    });
  }

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      this.apiReady.set(true);

      if (this.isLoggedIn() && this.isKioskMode()) {
        this.startReloadTimer();
      }
    });
  }

  startReloadTimer() {
    if (this.reloadTimer) {
      clearTimeout(this.reloadTimer);
    }

    this.reloadTimer = setTimeout(
      () => {
        location.reload();
      },
      1000 * 60 * 60 * 4, // 4 Minutes
    );
  }

  onDigitInput(event: KeyboardEvent) {
    let element: HTMLInputElement;
    let nextElement: HTMLInputElement;

    element = event.target as HTMLInputElement;

    let doCommence = false;

    if (event.key.match(/[0-9]/)) {
      element.value = event.key;
      let idx = parseInt(element.id.split('-')[2]);
      this.loginForm.controls['digit' + idx].setValue(event.key);
      doCommence = true;
    } else if (event.code === 'Backspace') {
      element.value = '';
      let idx = parseInt(element.id.split('-')[2]);
      this.loginForm.controls['digit' + idx].setValue('');
      doCommence = true;
    }
    event.preventDefault();

    if (!doCommence) return;

    this.loginForm.setErrors(null);

    if (event.code !== 'Backspace') nextElement = element.nextElementSibling as HTMLInputElement;
    if (event.code === 'Backspace')
      nextElement = element.previousElementSibling as HTMLInputElement;

    if (nextElement == null) {
      this.login();
    } else {
      nextElement.focus();
      nextElement.setSelectionRange(0, 1);
    }
  }

  login() {
    if (!this.loginForm.valid) return this.resetForm();

    this.loggingIn.set(true);
    this.loginError.set('');

    let pin =
      this.loginForm.value.digit1 +
      this.loginForm.value.digit2 +
      this.loginForm.value.digit3 +
      this.loginForm.value.digit4;

    this.api
      .pinLogin(pin)
      .then((result: TAGUser) => {
        this.loggingIn.set(false);
        this.loginError.set('');
        this.loginForm.setValue({
          digit1: '',
          digit2: '',
          digit3: '',
          digit4: '',
        });

        if (this.isKioskMode()) {
          this.startReloadTimer();
        }
      })
      .catch((reason: APIError) => {
        console.log(reason);
        this.loggingIn.set(false);
        if (reason.error == 2) {
          this.loginError.set('general.login.error.invalid');
        } else if (reason.error == 3 || reason.error == 7) {
          this.loginError.set('general.login.error.wronguser');
        } else {
          if (reason.status == 400 || reason.status == 403) this.loginError.set(reason.message);
          else this.loginError.set('Error ' + reason.error + ': ' + reason.message);
        }

        this.resetForm();
      });
  }

  resetForm() {
    this.loginForm.setValue({
      digit1: '',
      digit2: '',
      digit3: '',
      digit4: '',
    });

    this.pinInput1.nativeElement.focus();
  }

  logout() {
    this.api.logout();
    this.router.navigate(['workstation']);
  }

  closeMenu() {
    this.menu.toggleMenu(false);
  }
}
